<script setup lang="ts">
import { useAppStore } from "~/stores/app-store";
import type { Connection, ConnectionProvisionEvent } from "~/models/Connection";
import { param } from "ts-interface-checker";
import { usePopup } from "~/composables/usePopup";
import { faLock } from "@fortawesome/pro-regular-svg-icons";

const store = useAppStore();
const api = useApi();
const { echo } = useAppEcho();
const connections = computed(() => store.app?.connections ?? []);
const provisioning = ref<Record<string, ConnectionProvisionEvent>>({});
const deletable = ref();
const deleting = ref<boolean>(false);
const { refresh } = defineProps<{
    refresh: (showLoading: boolean) => Promise<void>;
}>();

onMounted(() => {
    echo.private(
        `app-events.${useUser().user.value?.account.id}.connections`,
    ).listen("ConnectionStatusUpdate", (event: ConnectionProvisionEvent) => {
        provisioning.value[event.id] = event;

        if (event.type === "provisioning_finished") {
            refresh(false).finally(() => {
                delete provisioning.value[event.id];
            });
        }
    });
});

onBeforeUnmount(() => {
    echo.leaveChannel(
        `app-events.${useUser().user.value?.account.id}.connections`,
    );
});

const interval = ref();

function salesforceReAuthorize(item: Connection) {
    const key = "salesforce-reauthorize";
    const fullUrl = new URL(item.connection.urls.redirect);

    const popupWindow = usePopup(fullUrl.href, key, {
        resizable: true,
        scrollbars: true,
        status: true,
        width: Math.round(window.outerWidth * 0.5),
        height: Math.round(window.outerHeight * 0.75),
    });

    function finalized() {
        useToast().add({
            title: "Connection authorized",
        });
        emit("connected");
        if (props.connected) {
            props.connected();
        }
        window?.removeEventListener("finalized", finalized, true);
        clearInterval(interval.value);
        interval.value = undefined;
        popupWindow?.close();
    }
    interval.value = setInterval(() => {
        if (popupWindow?.closed) {
            clearInterval(interval.value);
            interval.value = undefined;
        }
    }, 250);

    window?.addEventListener("finalized", finalized, true);
}

onBeforeUnmount(() => {
    if (interval.value) {
        clearInterval(interval.value);
        interval.value = undefined;
    }
});

function dropdown(item: Connection) {
    const dropdown = api.connections.dropdown(item, {
        deletable: deletable,
    });

    if (item.authorization?.type === "salesforce") {
        dropdown[0]?.splice(1, 0, {
            label: "Re-Authorize",
            icon: faLock,
            click: () => salesforceReAuthorize(item),
        });
    }

    return dropdown;
}

function deleteConnection() {
    deleting.value = true;
    api.connections.delete(deletable.value.uuid).finally(() => {
        deletable.value = undefined;
        deleting.value = false;
        refresh(false);
    });
}

const {
    data: core_apps,
    refresh: core_apps_refresh,
    status: core_apps_status,
} = useAsyncData(async () => {
    try {
        const data = await api.getEnumList<{ logo: string; icon: string }>(
            "core-apps",
        );

        return data.data
            .filter((item) => item.value !== param)
            .map((item) => item.label);
    } catch (e) {
        return [];
    }
});

function updateConnections(connections: Connection[]) {
    store.app.connections = connections;
}
</script>

<template>
    <div class="grid grid-cols-12 gap-7">
        <div
            v-if="connections.length"
            class="col-span-6 4xl:col-span-5 min-[2256px]:col-span-4 4xl:col-start-2 min-[2256px]:col-start-3 flex flex-col gap-2 w-full"
        >
            <ClientOnly>
                <slot
                    name="connections"
                    :connections="connections"
                    :provisioning="provisioning"
                    :updateConnections="updateConnections"
                >
                    <h3 class="font-display font-medium">Connections</h3>
                    <UAccordion
                        :items="
                            connections.map((item, index) => ({
                                ...item,
                                defaultOpen: connections.length === 1,
                            }))
                        "
                        class="w-full [&>button]:active:scale-100 gap-2"
                        :ui="{
                            container:
                                'flex rounded-md flex-col w-full bg-white ring-1 ring-inset ring-gray-300 shadow-sm',
                            item: {
                                base: 'px-3 py-2',
                            },
                        }"
                    >
                        <template #default="{ item, index, open }">
                            <div class="flex items-center px-3 gap-2">
                                <UButton
                                    color="white"
                                    variant="ghost"
                                    :padded="false"
                                    class="!scale-100 !bg-transparent py-2 flex-1 !ring-0 !shadow-none"
                                >
                                    <template #leading>
                                        <UButton color="white" variant="link">
                                            <BadgeAutomationStatus
                                                :name="item.label"
                                                :status="
                                                    item.active
                                                        ? 'active'
                                                        : 'warning'
                                                "
                                                icon-only
                                            />
                                        </UButton>
                                    </template>

                                    <BadgeConnectionProvisionStatus
                                        v-if="provisioning[item.uuid]"
                                        :status="provisioning[item.uuid].type"
                                        :data="provisioning[item.uuid].data"
                                    />

                                    <span class="truncate">{{
                                        item.label
                                    }}</span>
                                    <UBadge
                                        v-if="item.is_preset"
                                        :ui="{ rounded: 'rounded-full' }"
                                        color="gray"
                                        label="Preset"
                                    />
                                </UButton>
                                <UiDropdown :items="dropdown(item)">
                                    <template #default="{ toggle }">
                                        <UButton
                                            color="gray"
                                            icon="i-heroicons-ellipsis-horizontal-20-solid"
                                            variant="ghost"
                                            @click.stop="toggle"
                                        />
                                    </template>
                                </UiDropdown>
                                <UIcon
                                    name="i-heroicons-chevron-right-20-solid"
                                    class="w-5 h-5 ms-auto transform transition-transform duration-200"
                                    :class="[open && 'rotate-90']"
                                />
                            </div>
                        </template>
                        <template #item="{ item }">
                            <div class="p-2">
                                <UTabs
                                    :items="[
                                        {
                                            slot: 'metadata',
                                            label: 'Data Sync Timeline',
                                        },
                                    ]"
                                >
                                    <template #metadata>
                                        <AppConnectionProvisionMetadata
                                            class="border border-gray-300 rounded-md"
                                            :connection="item"
                                            no-label
                                        />
                                    </template>
                                </UTabs>
                            </div>
                        </template>
                    </UAccordion>
                </slot>
            </ClientOnly>
            <UIModalDelete
                :deleting="deleting"
                :items="deletable ? [deletable] : []"
                :object="['connection', 'connections']"
                :open="!!deletable"
                by="label"
                @delete="deleteConnection"
                @update:open="deletable = undefined"
            />
        </div>
        <div
            class="col-span-6 4xl:col-span-4 flex flex-col gap-2 w-full"
            :class="{
                'col-start-4 4xl:col-start-6': !connections.length,
            }"
        >
            <HdLoader :status="core_apps_status">
                <PartialAppSupport
                    :empty="!connections.length"
                    :core-apps="core_apps"
                />
            </HdLoader>
        </div>
    </div>
</template>
